/**
 * File: apps/qbench-cp/reactapp/src/App/store.js
 *
 * Purpose: Provides the app-wide authentication and session state store for the customer
 *     portal via React context and a reducer.
 *
 * Scope: Global app state — auth/login/MFA flow, current customer and division selection,
 *     field configuration, permissions and privileges, display settings, and portal branding.
 *
 * Tags: customer-portal
 *
 * Overview: Defines a reducer covering login/MFA validation, login success/failure, logout,
 *     customer and division selection, field-configuration loading, order-widget loading state,
 *     network error handling, and feature-flag/permission updates, backed by an initialState
 *     object. Each action type has its own handler function (handleLoggingIn, handleLogout,
 *     ...), collected into the actionHandlers lookup reducer dispatches through, rather than a
 *     single large switch statement. AppProvider exposes login, validateMFA, logout,
 *     refreshToken, and setPortalLogo as actions alongside the reducer state, persisting values
 *     such as divisionId and fieldConfigs to session/local storage and scheduling JWT refresh
 *     via setTimeout. It also redirects to a `redirect` query param destination once
 *     authenticated, and syncs division/customer changes into permissions and delinquency flags.
 *
 * Exports: useAppStore — hook returning the app context value ({ appState, appStateDispatch,
 *     login, logout, user, refreshToken, setPortalLogo, validateMFA }). AppProvider — context
 *     provider component wrapping the app with this store. reducer, initialState — exported for
 *     direct characterization-test coverage (App/store.reducer.test.js); no other module should
 *     import these, since App/store's public surface is useAppStore/AppProvider.
 */

import { STORAGE_KEY } from 'Dashboard/Pages/Orders/OrderList/DisplaySettings';
import isEmpty from 'lodash/isEmpty';
import { resetOrderRequestPersistentStorage } from 'Order/store';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { NotificationManager } from 'react-notifications';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EntityType } from 'shared/constants/entityTypes';
import { ENABLED_MODULES, SETTINGS } from 'shared/constants/fields';
import { parseFieldConfig } from 'shared/utils/fields';
import { logError } from 'shared/utils/logger';
import { millisecondsUntilJWTExpires, parseUser } from 'shared/utils/misc';
import { getViewPermissions } from 'shared/utils/permissions';
import { queryClient } from 'shared/utils/queryClient';
import { Auth, axiosinstance } from '../shared/utils/api';
import { safeSessionStorageSet } from 'shared/utils/functions';

// Finds the field config for `divisionId` among `user`'s portal_customers' divisions. Walks
// every customer in `user.portal_customers` looking for a division matching `divisionId`; does
// not stop at the first customer with a match, so if more than one customer has a division with
// that id, the last one visited (in portal_customers order) determines the returned
// fieldConfigs, not the first. Division ids are expected unique per customer in practice, but
// this function does not assume that. Persists fieldConfigs to sessionStorage only when a match
// is found for the customer currently being visited.
function findFieldConfigForDivision(user, divisionId) {
  if (!user) return undefined;
  let fieldConfigs;
  for (const customer of user.portal_customers) {
    const division = customer.divisions.find((d) => d.id === divisionId);
    if (division) {
      fieldConfigs = parseFieldConfig(division.field_config);
      safeSessionStorageSet('fieldConfigs', JSON.stringify(fieldConfigs));
    }
  }
  return fieldConfigs;
}

function handleLoggingIn(state) {
  return {
    ...state,
    loggingIn: true,
    loginErrorMessage: '',
    validatingMFA: false,
  };
}

function handleValidatingMfa(state) {
  return {
    ...state,
    validatingMFA: true,
  };
}

function handleValidatingMfaFailed(state, payload) {
  return {
    ...state,
    validatingMFA: false,
    validatingMFAErrorMessage: payload,
  };
}

function handleValidatingEmailMfa(state, payload) {
  return {
    ...state,
    validatingEmailMFA: true,
    emailMFAId: payload,
  };
}

function handleValidatingAuthenticatorAppMfa(state) {
  return {
    ...state,
    validatingAuthenticatorAppMFA: true,
  };
}

function handleLoginSuccess(state, payload) {
  if (!localStorage.getItem('customerId')) {
    localStorage.setItem('customerId', payload.customerId);
  }
  return {
    ...state,
    user: payload,
    isAuth: true,
    isInitializing: false,
    loggingIn: false,
    validatingEmailMFA: false,
    validatingAuthenticatorAppMFA: false,
    ...payload,
  };
}

function handleLoginFailed(state, payload) {
  return {
    ...state,
    isAuth: false,
    isInitializing: false,
    loggingIn: false,
    loginErrorMessage: payload,
  };
}

function handleLoginPageReset(state) {
  return {
    ...state,
    loggingIn: false,
    loginErrorMessage: '',
  };
}

function handleLogout(state, payload) {
  localStorage.removeItem('customerId');
  return {
    ...initialState,
    isInitializing: false,
    customPortalLogo: payload,
    preLoginSettings: state.preLoginSettings,
  };
}

function handleSetCustomerId(state, payload) {
  localStorage.setItem('customerId', payload);
  const customer = state.customers.find((c) => c.id === payload);
  let fieldConfigs = state.fieldConfigs;
  let division, divisionId;
  division = customer
    ? customer.divisions.find(
        (d) => d.id === parseInt(sessionStorage.getItem('divisionId')),
      )
    : null;
  if (!division && customer && customer.divisions) {
    division = customer.divisions[0];
  }
  if (division) {
    fieldConfigs = parseFieldConfig(division.field_config);
    safeSessionStorageSet('divisionId', division.id);
    safeSessionStorageSet('fieldConfigs', JSON.stringify(fieldConfigs));
    divisionId = division.id;
  }
  return {
    ...state,
    customer,
    divisionId,
    fieldConfigs,
    customerId: payload,
    delinquent: customer?.delinquent || false,
    invoiceGroupItemsByPanel:
      fieldConfigs?.SETTINGS?.[SETTINGS.INVOICE_GROUP_ITEMS_BY_PANEL],
  };
}

// Shared by handleSetDivision and handleSetDivisionCreateOrder: both look up the matching
// division's field_config out of the divisionData persisted to sessionStorage, parse it, and
// persist the parsed config back to sessionStorage under fieldConfigs.
function lookupAndPersistFieldConfig(divisionId) {
  const divisionData = JSON.parse(sessionStorage.getItem('divisionData'));
  let fieldConfigs;
  divisionData.map((item) => {
    if (item.id === divisionId) {
      fieldConfigs = parseFieldConfig(item.field_config);
      safeSessionStorageSet('fieldConfigs', JSON.stringify(fieldConfigs));
    }
  });
  return fieldConfigs;
}

function handleSetDivision(state, payload) {
  safeSessionStorageSet('divisionId', payload.id);
  const fieldConfigs =
    lookupAndPersistFieldConfig(payload.id) ?? state.fieldConfigs;
  return {
    ...state,
    divisionId: payload.id,
    fieldConfigs,
    fieldConfigLoaded: true,
    invoiceGroupItemsByPanel:
      fieldConfigs?.SETTINGS?.[SETTINGS.INVOICE_GROUP_ITEMS_BY_PANEL],
  };
}

function handleSetDivisionCreateOrder(state, payload) {
  safeSessionStorageSet('divisionId', payload);
  const fieldConfigs = lookupAndPersistFieldConfig(payload);
  return {
    ...state,
    fieldConfigs,
    divisionId: payload,
    invoiceGroupItemsByPanel:
      fieldConfigs?.SETTINGS?.[SETTINGS.INVOICE_GROUP_ITEMS_BY_PANEL],
  };
}

function handleSetDivisionId(state, payload) {
  const fieldConfigs = findFieldConfigForDivision(state.user, payload);
  safeSessionStorageSet('divisionId', payload);
  return {
    ...state,
    fieldConfigs,
    divisionId: payload,
    invoiceGroupItemsByPanel:
      fieldConfigs?.SETTINGS?.[SETTINGS.INVOICE_GROUP_ITEMS_BY_PANEL],
  };
}

function handleSetDivisionData(state, payload) {
  return {
    ...state,
    divisionData: payload,
    globalDivisionId: payload.find((div) => div.global_division)?.id,
    customers: state.customers.map((customer) => ({
      ...customer,
      divisions: (customer.divisions || []).map((division) => {
        const divisionData = payload.find((div) => div.id === division.id);
        return { ...division, ...divisionData };
      }),
    })),
  };
}

function handleFieldConfigLoaded(state, payload) {
  return {
    ...state,
    fieldConfigs: payload,
    fieldConfigLoaded: true,
  };
}

function handleOrdersLoaded(state, payload) {
  return {
    ...state,
    orders: payload,
    ordersWidgetLoaded: true,
    isLoading: false,
  };
}

function handleDashboardOrdersFetch(state) {
  return {
    ...state,
    ordersWidgetLoaded: false,
    isLoading: true,
  };
}

function handleDashboardOrderClicked(state, payload) {
  return {
    ...state,
    selectedOrder: payload,
  };
}

function handleStartLoading(state) {
  return {
    ...state,
    isLoadingCreateOrder: true,
  };
}

function handleEndLoading(state) {
  return {
    ...state,
    isLoadingCreateOrder: false,
  };
}

function handleOrderRequestEnter(state) {
  return {
    ...state,
    showFooter: false,
  };
}

function handleOrderRequestLeave(state) {
  return {
    ...state,
    showFooter: true,
  };
}

function handleResetCreateOrder(state, payload) {
  return {
    ...state,
    createNewOrder: payload,
  };
}

function handleNetworkError(state) {
  return {
    ...state,
    isNetworkError: true,
    isInitializing: false,
  };
}

function handleSetCustomPortalLogo(state, payload) {
  return {
    ...state,
    customPortalLogo: payload,
  };
}

function handleBillingEnabled(state, payload) {
  return {
    ...state,
    billingEnabled: payload, // For backwards compatibility
    permissions: {
      ...state.permissions,
      [ENABLED_MODULES.BILLING]: payload,
    },
  };
}

function handleTurnaroundsEnabled(state, payload) {
  return {
    ...state,
    turnaroundsEnabled: payload, // For backwards compatibility
    permissions: {
      ...state.permissions,
      [ENABLED_MODULES.TURNAROUNDS]: payload,
    },
  };
}

function handleFilterTatByAssayEnabled(state, payload) {
  return {
    ...state,
    filterTATByAssayEnabled: payload,
  };
}

function handleOrderTatToTestEnabled(state, payload) {
  return {
    ...state,
    orderTatToTestEnabled: payload,
  };
}

function handleInvoiceGroupItemsByPanel(state, payload) {
  return {
    ...state,
    invoiceGroupItemsByPanel: payload,
  };
}

function handleSetPreloginSettings(state, payload) {
  return {
    ...state,
    preLoginSettings: payload,
  };
}

function handleSetDisplaySettings(state, payload) {
  const displaySettings = {
    collapse: {
      ...state.displaySettings.collapse,
      ...payload.collapse,
    },
    detailView: {
      ...state.displaySettings.detailView,
      ...payload.detailView,
    },
    matchingSamplesOnly:
      payload.matchingSamplesOnly ?? state.displaySettings.matchingSamplesOnly,
  };
  localStorage.setItem('displaySettings', JSON.stringify(displaySettings));
  return {
    ...state,
    displaySettings,
  };
}

function handleSetPermissions(state, payload) {
  return {
    ...state,
    permissions: {
      ...state.permissions,
      ...payload,
      ...getViewPermissions(),
    },
  };
}

function handleSetUiPrivileges(state, payload) {
  // Privileges grouped by each Contact-Customer ID.
  return {
    ...state,
    privileges: payload,
  };
}

function handleSetDelinquent(state, payload) {
  const customerId = payload.customerId;
  const customer = payload.customers.find((c) => c.id === parseInt(customerId));
  return {
    ...state,
    delinquent: customer?.delinquent || false,
  };
}

const actionHandlers = {
  LOGGING_IN: handleLoggingIn,
  VALIDATING_MFA: handleValidatingMfa,
  VALIDATING_MFA_FAILED: handleValidatingMfaFailed,
  VALIDATING_EMAIL_MFA: handleValidatingEmailMfa,
  VALIDATING_AUTHENTICATOR_APP_MFA: handleValidatingAuthenticatorAppMfa,
  LOGIN_SUCCESS: handleLoginSuccess,
  LOGIN_FAILED: handleLoginFailed,
  LOGIN_PAGE_RESET: handleLoginPageReset,
  LOGOUT: handleLogout,
  SET_CUSTOMER_ID: handleSetCustomerId,
  SET_DIVISION: handleSetDivision,
  SET_DIVISION_CREATE_ORDER: handleSetDivisionCreateOrder,
  SET_DIVISION_ID: handleSetDivisionId,
  SET_DIVISION_DATA: handleSetDivisionData,
  FIELD_CONFIG_LOADED: handleFieldConfigLoaded,
  ORDERS_LOADED: handleOrdersLoaded,
  DASHBOARD_ORDERS_FETCH: handleDashboardOrdersFetch,
  DASHBOARD_ORDER_CLICKED: handleDashboardOrderClicked,
  START_LOADING: handleStartLoading,
  END_LOADING: handleEndLoading,
  ORDER_REQUEST_ENTER: handleOrderRequestEnter,
  ORDER_REQUEST_LEAVE: handleOrderRequestLeave,
  RESET_CREATE_ORDER: handleResetCreateOrder,
  NETWORK_ERROR: handleNetworkError,
  SET_CUSTOM_PORTAL_LOGO: handleSetCustomPortalLogo,
  BILLING_ENABLED: handleBillingEnabled,
  TURNAROUNDS_ENABLED: handleTurnaroundsEnabled,
  FILTER_TAT_BY_ASSAY_ENABLED: handleFilterTatByAssayEnabled,
  ORDER_TAT_TO_TEST_ENABLED: handleOrderTatToTestEnabled,
  INVOICE_GROUP_ITEMS_BY_PANEL: handleInvoiceGroupItemsByPanel,
  SET_PRELOGIN_SETTINGS: handleSetPreloginSettings,
  SET_DISPLAY_SETTINGS: handleSetDisplaySettings,
  SET_PERMISSIONS: handleSetPermissions,
  SET_UI_PRIVILEGES: handleSetUiPrivileges,
  SET_DELINQUENT: handleSetDelinquent,
};

const reducer = (state, action) => {
  const { type, payload } = action;
  // hasOwnProperty guard (not a bare `actionHandlers[type]` truthy check): actionHandlers is a
  // plain object literal, so an action `type` that happens to collide with an inherited
  // Object.prototype member name (e.g. 'toString', 'constructor', 'hasOwnProperty') would
  // otherwise resolve to that inherited function instead of falling through to the `default:
  // return state` behavior the original switch statement gave every unrecognized type.
  const handler = Object.prototype.hasOwnProperty.call(actionHandlers, type)
    ? actionHandlers[type]
    : undefined;
  return handler ? handler(state, payload) : state;
};

const initialState = {
  isAuth: false,
  user: {},
  portalAdmin: false,
  loggingIn: false,
  loginErrorMessage: '',
  validatingMFAErrorMessage: '',
  validatingMFA: false,
  validatingEmailMFA: false,
  validatingAuthenticatorAppMFA: false,
  isInitializing: true,
  selectedOrder: {},
  isLoading: false,
  isLoadingCreateOrder: false,
  showFooter: true,
  isNetworkError: false,
  fieldConfigLoaded: false,
  customer: {},
  customerId: null,
  customerIds: [],
  customers: [],
  divisionId: null,
  divisionIds: [],
  divisions: [],
  divisionData: [],
  fieldConfigs: {},
  customPortalLogo: null,
  billingEnabled: true,
  delinquent: false,
  createNewOrder: false,
  preLoginSettings: {},
  permissions: {},
  privileges: {},
  displaySettings: {
    collapse: {
      allOrders: false,
      allSamples: false,
      byOrders: [],
      bySamples: [],
    },
    detailView: {
      [EntityType.ORDER]: false,
      [EntityType.SAMPLE]: false,
      [EntityType.TEST]: false,
      [EntityType.ASSAY]: false,
    },
    matchingSamplesOnly: false,
  },
};

export { reducer, initialState };

const AppContext = createContext(initialState);
AppContext.displayName = 'AppContext';
export const useAppStore = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [appState, appStateDispatch] = useReducer(reducer, initialState);
  const { user, isAuth } = appState;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');

  const validateMFA = async (email, password) => {
    try {
      appStateDispatch({ type: 'VALIDATING_MFA' });
      const {
        data: { mfa_type = null, email_mfa_id = null },
      } = await Auth.validateMFA(email, password);

      switch (mfa_type) {
        case 'EMAIL':
          appStateDispatch({
            type: 'VALIDATING_EMAIL_MFA',
            payload: email_mfa_id,
          });
          break;
        case 'AUTHENTICATOR_APP':
          appStateDispatch({ type: 'VALIDATING_AUTHENTICATOR_APP_MFA' });
          break;
        default:
          login({ email, password });
          break;
      }
    } catch (err) {
      logError('validateMFA', err);
      appStateDispatch({
        type: 'VALIDATING_MFA_FAILED',
        payload:
          err?.data.message || 'Something went wrong when validating MFA.',
      });
    }
  };
  const login = async (options = {}) => {
    if (isEmpty(options)) throw new Error('Invalid login credentials');
    appStateDispatch({ type: 'LOGGING_IN' });

    const { email, password, authCode } = options;
    const authFn = () =>
      authCode ? Auth.samlLogin(authCode) : Auth.login(email, password);

    try {
      const {
        data: { access_token, csrf_token, user },
      } = await authFn();
      localStorage.setItem('csrfToken', csrf_token);
      axiosinstance.defaults.headers.common['Authorization'] =
        `Bearer ${access_token}`;

      setUIPrivileges(user);
      setUser(user);

      NotificationManager.listNotify.forEach((n) =>
        NotificationManager.remove({ id: n.id }),
      );

      setTimeout(() => {
        refreshToken();
      }, millisecondsUntilJWTExpires(access_token));
    } catch (err) {
      logError('login', err);
      appStateDispatch({
        type: 'LOGIN_FAILED',
        payload: err?.data?.message || 'Invalid email or password',
      });
    }
  };

  const logout = async () => {
    try {
      await Auth.logout();
    } catch (err) {
      // pass
    }

    const displaySettings = localStorage.getItem(STORAGE_KEY);
    localStorage.clear();
    localStorage.setItem(STORAGE_KEY, displaySettings);

    appStateDispatch({ type: 'LOGOUT' });
    queryClient.invalidateQueries();
    resetOrderRequestPersistentStorage();
    setPortalLogo();
  };

  const setDivision = useCallback(async () => {
    appStateDispatch({ type: 'SET_DIVISION_ID', payload: 3 });
  }, []);

  const setFields = useCallback(() => {
    const { BILLING, TURNAROUNDS } = ENABLED_MODULES;
    const {
      FILTER_TAT_BY_ASSAY_ENABLED,
      INVOICE_GROUP_ITEMS_BY_PANEL,
      ORDER_TAT_TO_TEST_ENABLED,
    } = SETTINGS;
    return axiosinstance.get(`/v1/fieldconfig`).then(({ data }) => {
      let fields = parseFieldConfig(data);
      safeSessionStorageSet('fieldConfigs', JSON.stringify(fields));
      localStorage.setItem('portalView', JSON.stringify(fields.PORTAL_VIEW));
      safeSessionStorageSet(
        'entityDisplayNames',
        JSON.stringify(fields.ENTITY_DISPLAY_NAMES),
      );

      appStateDispatch({
        type: 'SET_PERMISSIONS',
        payload: fields.ENABLED_MODULES,
      });
      appStateDispatch({ type: 'FIELD_CONFIG_LOADED', payload: fields });

      appStateDispatch({
        type: FILTER_TAT_BY_ASSAY_ENABLED,
        payload: fields.SETTINGS[FILTER_TAT_BY_ASSAY_ENABLED],
      });
      appStateDispatch({
        type: INVOICE_GROUP_ITEMS_BY_PANEL,
        payload: fields.SETTINGS[INVOICE_GROUP_ITEMS_BY_PANEL],
      });
      appStateDispatch({
        type: ORDER_TAT_TO_TEST_ENABLED,
        payload: fields.SETTINGS[ORDER_TAT_TO_TEST_ENABLED],
      });

      // These dispatches are kept for backwards compatibility reasons
      appStateDispatch({
        type: BILLING,
        payload: fields.ENABLED_MODULES[BILLING],
      });
      appStateDispatch({
        type: TURNAROUNDS,
        payload: fields.ENABLED_MODULES[TURNAROUNDS],
      });
    });
  }, [appState.divisionId]);

  const setUser = useCallback(
    async (user) => {
      try {
        const payload = parseUser(user);
        await setDivisionData();
        safeSessionStorageSet('divisionId', payload.divisionId);
        await setFields();
        appStateDispatch({
          type: 'SET_DELINQUENT',
          payload: payload,
        });
        appStateDispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, ...payload },
        });
      } catch (e) {
        appStateDispatch({
          type: 'LOGIN_FAILED',
          payload: 'Error parsing user data. Please try again.',
        });
      }
    },
    [setFields],
  );

  const setPortalLogo = useCallback(async () => {
    await axiosinstance
      .get('/v1/logo', { responseType: 'blob' })
      .then((res) => {
        appStateDispatch({
          type: 'SET_CUSTOM_PORTAL_LOGO',
          payload: res.data,
        });
      })
      .catch(() => {});
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      if (localStorage.getItem('csrfToken')) {
        const {
          data: { access_token, user },
        } = await Auth.refreshToken();
        axiosinstance.defaults.headers.common['Authorization'] =
          `Bearer ${access_token}`;

        setUIPrivileges(user);
        setUser(user);

        setTimeout(() => {
          refreshToken();
        }, millisecondsUntilJWTExpires(access_token));
      } else {
        logout();
      }
    } catch (err) {
      if (err) {
        if (appState.isAuth) {
          logout();
        }
        appStateDispatch({ type: 'LOGOUT' });
      } else {
        appStateDispatch({ type: 'NETWORK_ERROR' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setUser]);

  const setDivisionData = useCallback(async () => {
    await Auth.divisionData().then((res) => {
      let response = res.data.data;
      response.map(
        (item) => (item.field_config = parseFieldConfig(item.field_config)),
      );
      appStateDispatch({ type: 'SET_DIVISION_DATA', payload: response });
      safeSessionStorageSet('divisionData', JSON.stringify(response));
    });
  });

  const setUIPrivileges = (user) => {
    const payload = (user.portal_customers || [])
      .filter((c) => !c.portal_admin)
      .reduce(
        (acc, curr) => ({ ...acc, [curr.id]: curr.portal_permissions || [] }),
        {},
      );
    appStateDispatch({ type: 'SET_UI_PRIVILEGES', payload });
  };

  useEffect(() => {
    refreshToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuth && redirectTo && redirectTo.includes('/portal/')) {
      return navigate(redirectTo);
    }
  }, [isAuth, redirectTo]);

  return (
    <AppContext.Provider
      value={{
        appState,
        appStateDispatch,
        login,
        logout,
        user,
        refreshToken,
        setPortalLogo,
        validateMFA,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
